(function(root) {
  "use strict";

  var VERSION = "direct-shards-v1";
  if (root.MoonSutrasPlaceSearch && root.MoonSutrasPlaceSearch.version === VERSION) {
    return;
  }

  var API_BASE = "https://moon-sutras-place-search-preview.vercel.app/api/places";
  var FALLBACK_SEARCH_URL = API_BASE + "/search?q=";
  var ROUTING_MANIFEST_URL = API_BASE + "/routing-manifest-v1.json";
  var MAX_RESULTS = 8;
  var MIN_QUERY_CHARS = 3;

  var manifestPromise = null;

  var charMap = {
    "\u00c6": "AE",
    "\u00e6": "ae",
    "\u00d0": "D",
    "\u00f0": "d",
    "\u0110": "D",
    "\u0111": "d",
    "\u0141": "L",
    "\u0142": "l",
    "\u00d8": "O",
    "\u00f8": "o",
    "\u00de": "Th",
    "\u00fe": "th",
    "\u0152": "OE",
    "\u0153": "oe",
    "\u0131": "i",
    "\u1e9e": "SS",
    "\u00df": "ss"
  };

  function translateSpecialChars(value) {
    return String(value).replace(/[\u00c6\u00e6\u00d0\u00f0\u0110\u0111\u0141\u0142\u00d8\u00f8\u00de\u00fe\u0152\u0153\u0131\u1e9e\u00df]/g, function(ch) {
      return charMap[ch] || ch;
    });
  }

  function normalizeName(value) {
    var normalized = translateSpecialChars(value);
    if (typeof normalized.normalize === "function") {
      normalized = normalized.normalize("NFKD");
    }
    normalized = normalized.replace(/[\u0300-\u036f]/g, "");
    normalized = normalized.toLowerCase();
    normalized = normalized.replace(/[^a-z0-9]+/g, " ");
    return normalized.replace(/\s+/g, " ").trim();
  }

  function compactKey(value) {
    return String(value).replace(/ /g, "");
  }

  function routeKeyForQuery(query) {
    var compact = compactKey(normalizeName(query));
    if (compact.length < MIN_QUERY_CHARS) {
      return null;
    }
    return compact.slice(0, 3);
  }

  function fuzzyRouteCandidates(normalizedQuery) {
    var compact = compactKey(normalizedQuery);
    var candidates = {};
    var primary = compact.slice(0, 3);
    if (primary.length === 3) {
      candidates[primary] = true;
    }

    var chars = compact.split("");
    var limit = Math.min(4, chars.length - 1);
    for (var index = 0; index < limit; index += 1) {
      var swapped = chars.slice();
      var current = swapped[index];
      swapped[index] = swapped[index + 1];
      swapped[index + 1] = current;
      var candidate = swapped.join("").slice(0, 3);
      if (candidate.length === 3) {
        candidates[candidate] = true;
      }
    }

    return Object.keys(candidates);
  }

  function padShardId(shardId) {
    var value = String(shardId);
    return value.length === 1 ? "0" + value : value;
  }

  function abortError() {
    try {
      return new DOMException("The operation was aborted.", "AbortError");
    } catch (error) {
      var fallback = new Error("The operation was aborted.");
      fallback.name = "AbortError";
      return fallback;
    }
  }

  function fetchJson(url, signal) {
    return fetch(url, { signal: signal }).then(function(res) {
      if (!res.ok) {
        var error = new Error("Place search failed with " + res.status);
        error.status = res.status;
        throw error;
      }
      return res.json();
    });
  }

  function getRoutingManifest() {
    if (!manifestPromise) {
      manifestPromise = fetchJson(ROUTING_MANIFEST_URL).then(function(manifest) {
        if (!manifest || !manifest.route_to_shard) {
          throw new Error("Invalid place-search routing manifest.");
        }
        return manifest;
      }).catch(function(error) {
        manifestPromise = null;
        throw error;
      });
    }
    return manifestPromise;
  }

  function fallbackSearch(query, signal) {
    return fetchJson(FALLBACK_SEARCH_URL + encodeURIComponent(query), signal).then(function(data) {
      return Array.isArray(data) ? data.slice(0, MAX_RESULTS) : [];
    });
  }

  function fetchShard(shardId, query, mode, routes, signal) {
    var params = [
      "q=" + encodeURIComponent(query),
      "mode=" + encodeURIComponent(mode)
    ];

    routes.forEach(function(route) {
      params.push("route=" + encodeURIComponent(route));
    });

    return fetchJson(API_BASE + "/shard-" + padShardId(shardId) + "?" + params.join("&"), signal).then(function(data) {
      return Array.isArray(data) ? data.slice(0, MAX_RESULTS) : [];
    });
  }

  function searchDirect(query, manifest, signal) {
    var normalizedQuery = normalizeName(query);
    var compact = compactKey(normalizedQuery);
    var routeKey = routeKeyForQuery(query);

    if (!routeKey || compact.length < MIN_QUERY_CHARS) {
      return Promise.resolve([]);
    }

    var shardId = manifest.route_to_shard[routeKey];
    if (shardId === undefined || shardId === null) {
      return Promise.resolve([]);
    }

    if (signal && signal.aborted) {
      return Promise.reject(abortError());
    }

    return fetchShard(Number(shardId), query, "prefix", [routeKey], signal).then(function(results) {
      if (results.length || compact.length < 5) {
        return results.slice(0, MAX_RESULTS);
      }

      var candidates = fuzzyRouteCandidates(normalizedQuery).filter(function(candidate) {
        return candidate !== routeKey;
      }).sort();
      var shardRoutes = {};

      candidates.forEach(function(candidate) {
        var candidateShard = manifest.route_to_shard[candidate];
        if (candidateShard === undefined || candidateShard === null) {
          return;
        }
        candidateShard = String(candidateShard);
        if (!shardRoutes[candidateShard]) {
          shardRoutes[candidateShard] = [];
        }
        shardRoutes[candidateShard].push(candidate);
      });

      var fuzzyResults = [];
      var seenIds = {};
      var shardIds = Object.keys(shardRoutes).map(Number).sort(function(left, right) {
        return left - right;
      });

      return shardIds.reduce(function(chain, candidateShard) {
        return chain.then(function() {
          if (fuzzyResults.length >= MAX_RESULTS) {
            return null;
          }
          return fetchShard(candidateShard, query, "fuzzy", shardRoutes[String(candidateShard)], signal).then(function(shardResults) {
            shardResults.forEach(function(item) {
              var id = String(item && item.id);
              if (!id || seenIds[id] || fuzzyResults.length >= MAX_RESULTS) {
                return;
              }
              seenIds[id] = true;
              fuzzyResults.push(item);
            });
          });
        });
      }, Promise.resolve()).then(function() {
        return fuzzyResults.slice(0, MAX_RESULTS);
      });
    });
  }

  function search(query, options) {
    var signal = options && options.signal;
    return getRoutingManifest().then(function(manifest) {
      return searchDirect(query, manifest, signal);
    }).catch(function(error) {
      if ((signal && signal.aborted) || error.name === "AbortError") {
        throw error;
      }
      return fallbackSearch(query, signal);
    });
  }

  root.MoonSutrasPlaceSearch = {
    version: VERSION,
    search: search,
    normalizeName: normalizeName,
    compactKey: compactKey,
    routeKeyForQuery: routeKeyForQuery,
    fuzzyRouteCandidates: fuzzyRouteCandidates
  };
})(window);
